import { nanoid } from "nanoid";
import { z } from "zod";

const BaseDataSchema = z.object({
  id: z.string().nonempty(),
  selected: z.boolean(),
});
// type BaseDataSchema = z.infer<typeof BaseDataSchema>;

export const FrequencyTypeArr = [
  "Weekly",
  "Fortnightly",
  "Monthly",
  "Quarterly",
  "Yearly",
] as const;
const FrequencyTypeEnum = z.enum(FrequencyTypeArr);
export type FrequencyType = z.infer<typeof FrequencyTypeEnum>;

export const ExpenseTypesArr = [
  "rent",
  "utilities",
  "transport",
  "insurance",
  "loans",
  "medical",
  "personal",
  "education",
  "food",
  "children",
  "other",
] as const;
const ExpenseTypesEnum = z.enum(ExpenseTypesArr);
export type ExpenseType = z.infer<typeof ExpenseTypesEnum>;

export const ExpenseDataSchema = BaseDataSchema.extend({
  amount: z.number().min(0),
  frequency: FrequencyTypeEnum,
  description: z.string(),
  essential: z.boolean(),
});
export type ExpenseDataSchema = z.infer<typeof ExpenseDataSchema>;
export type ExpensesData = Record<ExpenseType, ExpenseDataSchema[]>;

export const IncomeDataSchema = BaseDataSchema.extend({
  amount: z.number().min(0),
  frequency: FrequencyTypeEnum,
  description: z.string(),
});
export type IncomeDataSchema = z.infer<typeof IncomeDataSchema>;

export const MortgageDataSchema = BaseDataSchema.extend({
  propertyValue: z.number().positive(),
  loanAmount: z.number().positive(),
  loanToValuePercentage: z.number().positive().max(100),
  depositAmount: z.number().positive(),
  loanTerm: z.number().min(0).max(60),
  interestRate: z.number().min(0).max(20),
  interestOnly: z.boolean(),
  repaymentFrequency: FrequencyTypeEnum
});
export type MortgageDataSchema = z.infer<typeof MortgageDataSchema>;

const defaultIncomeData = (): IncomeDataSchema => ({
  id: nanoid(),
  selected: true,
  amount: 0,
  frequency: "Fortnightly",
  description: "",
});

const initialIncomeData: IncomeDataSchema[] = [
  {
    id: nanoid(),
    selected: true,
    amount: 2000,
    frequency: "Fortnightly",
    description: "Work salary",
  },
  {
    id: nanoid(),
    selected: true,
    amount: 2000,
    frequency: "Fortnightly",
    description: "Partner's Work Salary",
  },
];

const defaultMortgageData = (): MortgageDataSchema => ({
    id: nanoid(),
    selected: true,
    propertyValue: 5000000,
    loanAmount: 400000,
    depositAmount: 100000,
    loanToValuePercentage: 80,
    interestRate: 6,
    loanTerm: 30,
    interestOnly: false,
    repaymentFrequency: 'Monthly'
});

const initialMortgageData: MortgageDataSchema[] = [
  {
    id: nanoid(),
    selected: true,
    propertyValue: 1000000,
    loanAmount: 800000,
    depositAmount: 200000,
    loanToValuePercentage: 80,
    interestRate: 6,
    loanTerm: 30,
    interestOnly: false,
    repaymentFrequency: 'Monthly'
  },
];

const defaultExpenseData = (): ExpenseDataSchema => ({
  id: nanoid(),
  selected: true,
  amount: 0,
  frequency: "Monthly",
  essential: true,
  description: "",
});

// TODO: Look at moving rent to housingData
const initialExpensesData: ExpensesData = {
  rent: [
    {
      id: nanoid(),
      selected: true,
      amount: 0,
      frequency: "Monthly",
      essential: true,
      description: "House",
    },
  ],
  utilities: [
    {
      id: nanoid(),
      selected: true,
      amount: 0,
      frequency: "Quarterly",
      essential: true,
      description: "Electricity",
    },
    {
      id: nanoid(),
      selected: true,
      amount: 0,
      frequency: "Quarterly",
      essential: true,
      description: "Gas",
    },
    {
      id: nanoid(),
      selected: true,
      amount: 0,
      frequency: "Quarterly",
      essential: true,
      description: "Water",
    },
    {
      id: nanoid(),
      selected: true,
      amount: 0,
      frequency: "Quarterly",
      essential: true,
      description: "Council Rates",
    },
    {
      id: nanoid(),
      selected: true,
      amount: 0,
      frequency: "Monthly",
      essential: true,
      description: "Strata Fees",
    },
    {
      id: nanoid(),
      selected: true,
      amount: 0,
      frequency: "Monthly",
      essential: true,
      description: "Internet Plan",
    },
    {
      id: nanoid(),
      selected: true,
      amount: 0,
      frequency: "Monthly",
      essential: true,
      description: "Mobile Plan",
    },
    {
      id: nanoid(),
      selected: true,
      amount: 0,
      frequency: "Monthly",
      essential: true,
      description: "Other",
    },
  ],

  transport: [
    {
      id: nanoid(),
      selected: true,
      amount: 0,
      frequency: "Monthly",
      essential: true,
      description: "Petrol",
    },
    {
      id: nanoid(),
      selected: true,
      amount: 0,
      frequency: "Monthly",
      essential: true,
      description: "Registration",
    },
    {
      id: nanoid(),
      selected: true,
      amount: 0,
      frequency: "Yearly",
      essential: true,
      description: "Servicing",
    },
    {
      id: nanoid(),
      selected: true,
      amount: 0,
      frequency: "Monthly",
      essential: true,
      description: "Public Transport",
    },
    {
      id: nanoid(),
      selected: true,
      amount: 0,
      frequency: "Monthly",
      essential: true,
      description: "Other",
    },
  ],

  insurance: [
    {
      id: nanoid(),
      selected: true,
      amount: 0,
      frequency: "Monthly",
      essential: true,
      description: "Home",
    },
    {
      id: nanoid(),
      selected: true,
      amount: 0,
      frequency: "Monthly",
      essential: true,
      description: "Car",
    },
    {
      id: nanoid(),
      selected: true,
      amount: 0,
      frequency: "Monthly",
      essential: true,
      description: "Health",
    },
    {
      id: nanoid(),
      selected: true,
      amount: 0,
      frequency: "Monthly",
      essential: true,
      description: "Pet",
    },
    {
      id: nanoid(),
      selected: true,
      amount: 0,
      frequency: "Monthly",
      essential: true,
      description: "Life",
    },
    {
      id: nanoid(),
      selected: true,
      amount: 0,
      frequency: "Monthly",
      essential: true,
      description: "Other",
    },
  ],

  food: [
    {
      id: nanoid(),
      selected: true,
      amount: 0,
      frequency: "Monthly",
      essential: true,
      description: "Groceries",
    },
    {
      id: nanoid(),
      selected: true,
      amount: 0,
      frequency: "Monthly",
      essential: false,
      description: "Takeaway",
    },
    {
      id: nanoid(),
      selected: true,
      amount: 0,
      frequency: "Monthly",
      essential: false,
      description: "Restaurants",
    },
    {
      id: nanoid(),
      selected: true,
      amount: 0,
      frequency: "Monthly",
      essential: true,
      description: "Pet Food",
    },
    {
      id: nanoid(),
      selected: true,
      amount: 0,
      frequency: "Monthly",
      essential: true,
      description: "Other",
    },
  ],

  medical: [
    {
      id: nanoid(),
      selected: true,
      amount: 0,
      frequency: "Yearly",
      essential: true,
      description: "Dental",
    },
    {
      id: nanoid(),
      selected: true,
      amount: 0,
      frequency: "Monthly",
      essential: true,
      description: "Appointments",
    },
    {
      id: nanoid(),
      selected: true,
      amount: 0,
      frequency: "Monthly",
      essential: true,
      description: "Medicines",
    },
    {
      id: nanoid(),
      selected: true,
      amount: 0,
      frequency: "Yearly",
      essential: true,
      description: "Vet",
    },
    {
      id: nanoid(),
      selected: true,
      amount: 0,
      frequency: "Monthly",
      essential: true,
      description: "Other",
    },
  ],

  personal: [
    {
      id: nanoid(),
      selected: true,
      amount: 0,
      frequency: "Monthly",
      essential: true,
      description: "Gym",
    },
    {
      id: nanoid(),
      selected: true,
      amount: 0,
      frequency: "Monthly",
      essential: true,
      description: "Hair",
    },
    {
      id: nanoid(),
      selected: true,
      amount: 0,
      frequency: "Yearly",
      essential: true,
      description: "Clothing & Shoes",
    },
    {
      id: nanoid(),
      selected: true,
      amount: 0,
      frequency: "Yearly",
      essential: true,
      description: "Online Subscriptions",
    },
    {
      id: nanoid(),
      selected: true,
      amount: 0,
      frequency: "Monthly",
      essential: true,
      description: "Activities",
    },
    {
      id: nanoid(),
      selected: true,
      amount: 0,
      frequency: "Monthly",
      essential: true,
      description: "Other",
    },
  ],

  education: [
    {
      id: nanoid(),
      selected: true,
      amount: 0,
      frequency: "Monthly",
      essential: true,
      description: "University",
    },
    {
      id: nanoid(),
      selected: true,
      amount: 0,
      frequency: "Monthly",
      essential: true,
      description: "Schooling",
    },
    {
      id: nanoid(),
      selected: true,
      amount: 0,
      frequency: "Monthly",
      essential: true,
      description: "Other",
    },
  ],

  children: [
    {
      id: nanoid(),
      selected: true,
      amount: 0,
      frequency: "Monthly",
      essential: true,
      description: "Childcare",
    },
    {
      id: nanoid(),
      selected: true,
      amount: 0,
      frequency: "Monthly",
      essential: true,
      description: "School Fees",
    },
    {
      id: nanoid(),
      selected: true,
      amount: 0,
      frequency: "Monthly",
      essential: true,
      description: "Activities",
    },
    {
      id: nanoid(),
      selected: true,
      amount: 0,
      frequency: "Monthly",
      essential: true,
      description: "Other",
    },
  ],

  loans: [
    {
      id: nanoid(),
      selected: true,
      amount: 0,
      frequency: "Monthly",
      essential: true,
      description: "Car Loan",
    },
  ],

  other: [
    {
      id: nanoid(),
      selected: true,
      amount: 0,
      frequency: "Monthly",
      essential: true,
      description: "Other",
    },
  ],
};

export {
  defaultIncomeData,
  initialIncomeData,
  defaultMortgageData,
  initialMortgageData,
  defaultExpenseData,
  initialExpensesData,
};
